import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { IAumicoCompanySummary } from '../../models/integration/company-mapping-suggestion.model';

/** Min characters before the search dropdown opens: matches Zefix UX. */
const SEARCH_MIN_CHARS = 3;

/** Input debounce for the search term: feels live, avoids per keystroke churn. */
const SEARCH_DEBOUNCE_MS = 200;

/**
 * Presentational (dumb) Aumico-Company search input + dropdown.
 *
 * Extracted verbatim from the D365 BC single-edit mapping modal so every vendor
 * mapping flow (D365 BC, Abacus, …) gets the identical search UX: highlighted
 * matches, a result-count header, a loading row and an error row.
 *
 * It does NOT fetch anything itself — the catalog of selectable Aumico Companies
 * is passed in via `options`, with `isLoading` / `hasError` reflecting the parent's
 * fetch state. All filtering, debouncing, dropdown AND the staged "selected" card
 * are pure UI and live here. The selection is surfaced via the two-way bindable
 * `selectedCompany` ([(selectedCompany)]); the parent owns only the commit step.
 */
@Component({
    selector: 'aumico-integrations-search-company-input-field',
    templateUrl: './integrations-search-company-input-field.component.html',
    styleUrls: ['./integrations-search-company-input-field.component.scss'],
})
export class IntegrationsSearchCompanyInputFieldComponent implements OnInit, OnChanges, OnDestroy {
    /** Full catalog of selectable Aumico Companies. Null while the parent is still fetching. */
    @Input() public options: IAumicoCompanySummary[] | null = null;

    /** True while the parent is loading the catalog — renders the loading row. */
    @Input() public isLoading = false;

    /** True when the parent's catalog fetch failed — renders the error row. */
    @Input() public hasError = false;

    /** Optional already-translated placeholder. Falls back to the shared search placeholder key. */
    @Input() public placeholder: string | null = null;

    /**
     * Currently selected Aumico Company. When set, a staged card (with a "Change"
     * action) replaces the search input. Two-way bindable via `[(selectedCompany)]`.
     */
    @Input() public selectedCompany: IAumicoCompanySummary | null = null;
    @Output() public selectedCompanyChange = new EventEmitter<IAumicoCompanySummary | null>();

    /**
     * Hides the staged card's "Change" action so the selection can't be cleared
     * (e.g. Abacus edit mode, where the Aumico Company is locked to the mapping).
     */
    @Input() public locked = false;

    /** Driven by the input's `[ngModel]`; pushed into `searchTerm$` on change. */
    public searchTerm = '';
    public readonly searchTerm$ = new BehaviorSubject<string>('');

    /**
     * Reactive search results stream:
     *  - on every searchTerm$ emission, filter the (in-memory) options client side
     *  - emit `null` while the user is below the min char threshold (hides dropdown)
     */
    public searchResults$!: Observable<IAumicoCompanySummary[] | null>;

    /** Whether to render the dropdown surface. Toggled by focus/blur + threshold. */
    public showDropdown = false;

    /** Options mirrored into a stream so `searchResults$` recomputes when the parent supplies them. */
    private readonly options$ = new BehaviorSubject<IAumicoCompanySummary[]>([]);

    private readonly destroy$ = new Subject<void>();

    public ngOnInit(): void {
        const debouncedTerm$ = this.searchTerm$.pipe(
            debounceTime(SEARCH_DEBOUNCE_MS),
            distinctUntilChanged(),
        );

        this.searchResults$ = combineLatest([this.options$, debouncedTerm$]).pipe(
            map(([catalog, term]) => {
                const trimmed = term.trim();
                if (trimmed.length < SEARCH_MIN_CHARS) return null;
                const needle = trimmed.toLowerCase();
                return catalog
                    .filter((c) => c.name.toLowerCase().includes(needle))
                    .slice(0, 50);
            }),
            takeUntil(this.destroy$),
        );
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['options'] != null) {
            this.options$.next(this.options ?? []);
        }
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.searchTerm$.complete();
        this.options$.complete();
    }

    // ── Search input handlers ───────────────────────────────────────────────

    public onSearchChange(term: string): void {
        this.searchTerm = term;
        this.searchTerm$.next(term ?? '');
        this.showDropdown = (term ?? '').trim().length >= SEARCH_MIN_CHARS;
    }

    public onSearchFocus(): void {
        if (this.searchTerm.trim().length >= SEARCH_MIN_CHARS) {
            this.showDropdown = true;
        }
    }

    public onSearchBlur(): void {
        // Defer so a click on a dropdown row registers before we hide it.
        setTimeout(() => {
            this.showDropdown = false;
        }, 120);
    }

    /** User clicked a search dropdown row: stage the selection (shows the staged card) and reset the input. */
    public onSelectSearchResult(aumicoCompany: IAumicoCompanySummary): void {
        this.selectedCompany = aumicoCompany;
        this.selectedCompanyChange.emit(aumicoCompany);
        this.searchTerm = '';
        this.searchTerm$.next('');
        this.showDropdown = false;
    }

    /** User clicked "Change" on the staged card: clear the selection and reveal a fresh search input. */
    public onChangeSelection(): void {
        this.selectedCompany = null;
        this.selectedCompanyChange.emit(null);
    }

    /**
     * Adds <mark> tags around the search term inside a name, for the dropdown UI.
     * The result is bound via [innerHTML], so the source text is HTML-escaped first
     * and only the injected <mark> tags are trusted — names containing <, >, & or
     * quotes render verbatim instead of being interpreted (or stripped) as markup.
     */
    public highlightMatch(text: string): string {
        const safe = this.escapeHtml(text);
        const query = this.searchTerm.trim();
        if (query.length < 2) return safe;
        // Escape the query for HTML too (so it matches the escaped text), then for regex.
        const escapedQuery = this.escapeHtml(query).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        return safe.replace(new RegExp(`(${escapedQuery})`, 'gi'), '<mark>$1</mark>');
    }

    private escapeHtml(value: string): string {
        return value
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }
}
